import { Typography } from "@mui/material";
import createStyles from "@mui/styles/createStyles";
import makeStyles from "@mui/styles/makeStyles";
import { OLButton } from "OLButton";
import { useCampaigns } from "api/campaigns";
import { useAppDispatch, useAppSelector } from "app/hooks";
import { TopHeader } from "components/Header/TopHeader/TopHeader";
import { LoadingInterceptor } from "components/LoadingInterceptor/LoadingInterceptor";
import { OLTheme } from "contexts/Theme/ThemeProvider";
import { useMessage } from "hooks/intl";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  selectImportedCampaigns,
  setImportedCampaigns,
} from "reduxState/campaigns/campaigns";
import {
  CampaignBEData,
  UnSuccessfulImportedCampaignData,
} from "types/campaigns";
import { links } from "utilities/links";
import { SuccessfulImports } from "./SuccessfulImports";
import { UnSuccessfulImports } from "./UnSuccessfulImports";
import { formatImportedCampaigns } from "./utils";

export const useStyles = makeStyles((theme: OLTheme) =>
  createStyles({
    wrapper: {
      width: 872,
      margin: "0 auto",
      padding: theme.spacing(12, 0),
    },
    tableTitle: {
      color: theme.gray800,
      fontWeight: 400,
      marginBottom: theme.spacing(6),
    },
    link: {
      color: theme.primary500,
      fontWeight: 500,
      fontSize: 15,
      textTransform: "uppercase",
      textDecoration: "none",
    },
    linkWrapper: {
      display: "flex",
      justifyContent: "center",
      width: "100%",
    },
    nameTdWrapper: {
      display: "flex",
      alignItems: "center",
      maxWidth: "100%",
    },
    name: {
      whiteSpace: "nowrap",
      textOverflow: "ellipsis",
      overflow: "hidden",
      marginRight: theme.spacing(4),
    },
    icon: {
      marginRight: theme.spacing(4),
      minWidth: 20,
      minHeight: 20,
    },
    buttonWrapper: {
      marginTop: theme.spacing(12),
      display: "flex",
      justifyContent: "flex-end",
    },
    warnIcon: {
      color: theme.orange500,
      marginRight: theme.spacing(4),
      minWidth: 20,
      minHeight: 20,
    },
    errorIcon: {
      color: theme.red500,
    },
    simulateInfo: {
      fontSize: 14,
      marginTop: theme.spacing(6),
    },
  }),
);

type ImportedCampaignsData = {
  saved: CampaignBEData[];
  unSaved: UnSuccessfulImportedCampaignData[];
};

export const AutomationsImportList = () => {
  const dispatch = useAppDispatch();
  const [campaignsData, setCampaignsData] = useState<ImportedCampaignsData>({
    saved: [],
    unSaved: [],
  });

  const [isLoading, setIsLoading] = useState(true);
  const { message } = useMessage();
  const classes = useStyles();
  const navigate = useNavigate();
  const { addCampaignBatch } = useCampaigns();
  const importedCampaigns = useAppSelector(selectImportedCampaigns);
  const areImportedCampaigns = importedCampaigns.length > 0;

  useEffect(() => {
    if (areImportedCampaigns) {
      const formattedCampaigns = formatImportedCampaigns(importedCampaigns);

      addCampaignBatch(formattedCampaigns).then((resp) => {
        // Update imported campaigns
        if (resp) {
          dispatch(
            setImportedCampaigns(
              resp?.map((campaignData) => {
                if ("campaignId" in campaignData) {
                  return {
                    ...campaignData.data,
                    campaignId: campaignData.campaignId,
                  };
                } else {
                  return campaignData.data;
                }
              }),
            ),
          );

          const sortedCampaigns: ImportedCampaignsData = {
            saved: [],
            unSaved: [],
          };

          resp?.forEach((campaignData, index) => {
            if ("campaignId" in campaignData) {
              return sortedCampaigns.saved.push({
                ...campaignData.data,
                campaignId: campaignData.campaignId,
              } as CampaignBEData);
            }
            sortedCampaigns.unSaved.push({
              ...campaignData,
              importedId: index,
            });
          });

          setCampaignsData(sortedCampaigns);
          setIsLoading(false);
        }
      });
    } else {
      navigate(links.dashboard);
    }

    // eslint-disable-next-line
  }, []);

  const areSuccessImports = campaignsData.saved.length > 0;
  const areUnSuccessImports = campaignsData.unSaved.length > 0;

  return (
    <>
      <TopHeader
        title={message({ id: "automationsImportReview" })}
        clearLink={links.automationsList}
      />
      <div className={classes.wrapper}>
        <LoadingInterceptor isLoading={isLoading}>
          {areSuccessImports && (
            <>
              <SuccessfulImports campaigns={campaignsData.saved} />
              <Typography className={classes.simulateInfo}>
                {message({ id: "importAutomationsSimulateInfo" })}
              </Typography>
            </>
          )}
          {areUnSuccessImports && (
            <UnSuccessfulImports campaigns={campaignsData.unSaved} />
          )}
        </LoadingInterceptor>
        <div className={classes.buttonWrapper}>
          <OLButton
            variant="contained"
            onClick={() => navigate(links.automationsList)}
            data-test-id="view-list-of-campaigns"
          >
            {message({ id: "viewListOfAutomations" })}
          </OLButton>
        </div>
      </div>
    </>
  );
};
